/**
 * AdminSisters — /admin/sisters
 *
 * Live health board for every registered sister site (41 today).
 * Fuses three data sources into one row-per-sister view:
 *   1. Canonical portal registry (slug, portal_name, domain, geography)
 *   2. `oauth_clients` collection (does a `<slug>-web` client exist? are
 *      prod redirect URIs whitelisted?)
 *   3. `oidc_auto_preview_log` collection (recent SSO activity by host)
 *
 * The row status tells ops exactly what to do:
 *   • pending          — no OIDC client yet — mint one
 *   • wired-no-prod    — client exists but prod redirect_uri missing
 *   • wired-idle       — fully wired but no SSO hits — sister hasn't
 *                        integrated yet
 *   • active           — wired + at least one auto-approve hit
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, ArrowLeft, ShieldCheck, ShieldOff, ShieldAlert,
  Users, Globe2, Clock, Sparkles, Search, ExternalLink, Zap,
  Activity, AlertCircle, CheckCircle2, XCircle, HelpCircle,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";

const STATUS_STYLE = {
  active:          { bg: "bg-emerald-50",   ring: "ring-emerald-300", txt: "text-emerald-700",  dot: "bg-emerald-500" },
  "wired-idle":    { bg: "bg-amber-50",     ring: "ring-amber-300",   txt: "text-amber-700",    dot: "bg-amber-500"   },
  "wired-no-prod": { bg: "bg-orange-50",    ring: "ring-orange-300",  txt: "text-orange-700",   dot: "bg-orange-500"  },
  pending:         { bg: "bg-rose-50",      ring: "ring-rose-300",    txt: "text-rose-700",     dot: "bg-rose-500"    },
};

const STATUS_LABEL = {
  active: "Active",
  "wired-idle": "Wired · idle",
  "wired-no-prod": "Missing prod URL",
  pending: "Pending mint",
};

const SSO_VERDICT_STYLE = {
  green:  { bg: "bg-emerald-50", txt: "text-emerald-700", ring: "ring-emerald-300", dot: "bg-emerald-500", label: "SSO green", icon: CheckCircle2 },
  amber:  { bg: "bg-amber-50",   txt: "text-amber-700",   ring: "ring-amber-300",   dot: "bg-amber-500",   label: "SSO amber", icon: AlertCircle },
  red:    { bg: "bg-rose-50",    txt: "text-rose-700",    ring: "ring-rose-300",    dot: "bg-rose-500",    label: "SSO red",   icon: XCircle },
};

function fmtRelative(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.round((now - then) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

export default function AdminSisters() {
  const { user, loading: authLoading } = useAuth();
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [ssoFilter, setSsoFilter] = useState("all");
  const [minting, setMinting] = useState(null);
  const [auditingSlug, setAuditingSlug] = useState(null);
  const [auditingAll, setAuditingAll] = useState(false);
  const [expandedAudit, setExpandedAudit] = useState({});   // slug → audit result
  const [auditResults, setAuditResults] = useState({});     // slug → latest full audit result (in-session)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get("/content-engine/admin/sisters");
      setRows(r.data.sisters || []);
      setSummary(r.data.summary || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const mintClient = async (slug) => {
    if (!slug) return;
    if (!window.confirm(`Mint <${slug}-web> public PKCE OIDC client?\n\nWill whitelist the portal's prod + www + Emergent preview redirect URIs. Idempotent.`)) return;
    setMinting(slug);
    try {
      const r = await http.post("/content-engine/admin/sisters/mint-client", { slug });
      if (r.data.already_registered) toast.info(`Already minted: ${r.data.client_id}`);
      else toast.success(`Minted ${r.data.client_id} → ${r.data.redirect_uris.length} redirect URI(s)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setMinting(null);
    }
  };

  const auditSister = async (slug) => {
    if (!slug) return;
    setAuditingSlug(slug);
    try {
      const r = await http.post(`/content-engine/admin/sisters/${slug}/sso-audit`);
      setAuditResults((prev) => ({ ...prev, [slug]: r.data }));
      setExpandedAudit((prev) => ({ ...prev, [slug]: true }));
      const v = r.data.verdict;
      const msg = `${slug}: ${r.data.pass_count}/${r.data.total_steps} passed`;
      if (v === "green") toast.success(`✓ ${msg}`);
      else if (v === "amber") toast.warning(`△ ${msg} (see details)`);
      else toast.error(`✗ ${msg} (see details)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setAuditingSlug(null);
    }
  };

  const auditAll = async () => {
    if (!window.confirm("Run the 5-step OIDC audit for every registered sister? Takes ~15s for the full network.")) return;
    setAuditingAll(true);
    try {
      const r = await http.post("/content-engine/admin/sisters/sso-audit-all");
      toast.success(`Audit complete · ${r.data.green} green · ${r.data.amber} amber · ${r.data.red} red · (${r.data.skipped} skipped) in ${r.data.duration_ms}ms`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setAuditingAll(false);
    }
  };

  useEffect(() => { if (user) load(); }, [user, load]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const ql = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (ssoFilter !== "all") {
        const v = r.latest_sso_audit?.verdict || "unaudited";
        if (v !== ssoFilter) return false;
      }
      if (!ql) return true;
      return (
        r.slug.includes(ql) ||
        r.portal_name.toLowerCase().includes(ql) ||
        r.client_id.includes(ql) ||
        (r.domain || "").toLowerCase().includes(ql) ||
        (r.latest_grant?.host || "").toLowerCase().includes(ql)
      );
    });
  }, [rows, q, statusFilter, ssoFilter]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SocialPulseHeader />
        <div className="max-w-3xl mx-auto py-32 px-6 text-center">
          <h2 className="font-serif text-2xl text-slate-900">Sign in required</h2>
          <Link to="/sign-in?next=/admin/sisters" className="mt-4 inline-block rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold px-5 py-2">
            Sign in →
          </Link>
        </div>
        <SocialPulseFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-sisters-page">
      <SocialPulseHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* header */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
              <Link to="/admin/content-engine/report" className="inline-flex items-center gap-1 hover:text-slate-800">
                <ArrowLeft className="w-3.5 h-3.5" />
                Content-engine report
              </Link>
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl text-slate-900 font-bold">Sister sites — health board</h1>
            <p className="text-sm text-slate-600 mt-1">
              Live fusion of the portal registry + OIDC clients + preview auto-approve log. 41 sisters expected.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={auditAll}
              disabled={auditingAll || loading}
              data-testid="admin-sisters-audit-all"
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2"
              title="Run the 5-step OIDC audit for every registered sister"
            >
              {auditingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Activity className="w-3.5 h-3.5" />}
              Audit all SSO
            </button>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="admin-sisters-refresh"
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Refresh
            </button>
          </div>
        </div>

        {/* summary cards */}
        {summary && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-3" data-testid="admin-sisters-summary">
              <SummaryPill icon={<Users className="w-4 h-4" />} label="Total portals" value={summary.total_portals} tone="slate" />
              <SummaryPill icon={<ShieldCheck className="w-4 h-4" />} label="Active" value={summary.active} tone="emerald" />
              <SummaryPill icon={<Clock className="w-4 h-4" />} label="Wired · idle" value={summary.wired_idle} tone="amber" />
              <SummaryPill icon={<ShieldAlert className="w-4 h-4" />} label="Missing prod" value={summary.wired_no_prod} tone="orange" />
              <SummaryPill icon={<ShieldOff className="w-4 h-4" />} label="Pending mint" value={summary.pending} tone="rose" />
              <SummaryPill icon={<Sparkles className="w-4 h-4" />} label="Native-heavy" value={summary.native_heavy} tone="violet" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="admin-sisters-sso-summary">
              <SummaryPill icon={<CheckCircle2 className="w-4 h-4" />} label="SSO green" value={summary.sso_green ?? 0} tone="emerald" />
              <SummaryPill icon={<AlertCircle className="w-4 h-4" />} label="SSO amber" value={summary.sso_amber ?? 0} tone="amber" />
              <SummaryPill icon={<XCircle className="w-4 h-4" />} label="SSO red" value={summary.sso_red ?? 0} tone="rose" />
              <SummaryPill icon={<HelpCircle className="w-4 h-4" />} label="Unaudited" value={summary.sso_unaudited ?? 0} tone="slate" />
            </div>
          </>
        )}

        {/* filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="search"
              placeholder="Search slug, name, domain, client_id, preview host…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="admin-sisters-search"
              className="w-full rounded-full ring-1 ring-slate-200 focus:ring-slate-400 bg-white pl-10 pr-4 py-2 text-sm outline-none"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="admin-sisters-status-filter"
            className="rounded-full ring-1 ring-slate-200 bg-white px-4 py-2 text-sm outline-none"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="wired-idle">Wired · idle</option>
            <option value="wired-no-prod">Missing prod URL</option>
            <option value="pending">Pending mint</option>
          </select>
          <select
            value={ssoFilter}
            onChange={(e) => setSsoFilter(e.target.value)}
            data-testid="admin-sisters-sso-filter"
            className="rounded-full ring-1 ring-slate-200 bg-white px-4 py-2 text-sm outline-none"
          >
            <option value="all">All SSO verdicts</option>
            <option value="green">SSO green</option>
            <option value="amber">SSO amber</option>
            <option value="red">SSO red</option>
            <option value="unaudited">Unaudited</option>
          </select>
        </div>

        {/* table */}
        <div className="bg-white rounded-2xl ring-1 ring-slate-200 overflow-hidden">
          {loading && !rows ? (
            <div className="p-12 text-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500 text-sm">
              {q || statusFilter !== "all" ? "No sisters match your filters." : "No sister sites registered yet."}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((r) => {
                const style = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
                const sso = r.latest_sso_audit;
                const ssoStyle = sso ? SSO_VERDICT_STYLE[sso.verdict] : null;
                const SsoIcon = ssoStyle?.icon;
                const isExpanded = !!expandedAudit[r.slug];
                const fullAudit = auditResults[r.slug];
                return (
                  <li key={r.slug} className="p-4 hover:bg-slate-50" data-testid={`admin-sisters-row-${r.slug}`}>
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-serif text-base font-bold text-slate-900 truncate">{r.portal_name}</h3>
                          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${style.bg} ${style.txt} ring-1 ${style.ring}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                            {STATUS_LABEL[r.status] || r.status}
                          </span>
                          {ssoStyle && SsoIcon && (
                            <button
                              type="button"
                              onClick={() => setExpandedAudit((prev) => ({ ...prev, [r.slug]: !prev[r.slug] }))}
                              data-testid={`admin-sisters-sso-badge-${r.slug}`}
                              title={`Last checked ${fmtRelative(sso.checked_at)} · ${sso.pass_count}/${sso.total_steps} passed · click for details`}
                              className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${ssoStyle.bg} ${ssoStyle.txt} ring-1 ${ssoStyle.ring} hover:opacity-80`}
                            >
                              <SsoIcon className="w-2.5 h-2.5" />
                              {ssoStyle.label} · {sso.pass_count}/{sso.total_steps}
                            </button>
                          )}
                          {!ssoStyle && r.status !== "pending" && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-50 text-slate-500 ring-1 ring-slate-200">
                              <HelpCircle className="w-2.5 h-2.5" /> SSO unaudited
                            </span>
                          )}
                          {r.native_dedupe.heavy && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 ring-1 ring-violet-200">
                              <Sparkles className="w-2.5 h-2.5" /> Native-heavy
                            </span>
                          )}
                          {r.latest_grant?.denied && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-200">
                              <ShieldOff className="w-2.5 h-2.5" /> Host denied
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                          <code className="font-mono text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{r.slug}</code>
                          <span>·</span>
                          <code className="font-mono text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{r.client_id}</code>
                          <span>·</span>
                          <span className="inline-flex items-center gap-1"><Globe2 className="w-3 h-3" /> {r.geography || "—"}</span>
                          {r.domain && (
                            <a href={r.domain} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-slate-800">
                              <ExternalLink className="w-3 h-3" />
                              {r.domain.replace(/^https?:\/\//, "")}
                            </a>
                          )}
                          {sso?.checked_at && (
                            <>
                              <span>·</span>
                              <span className="text-[10px]">audit {fmtRelative(sso.checked_at)}</span>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="text-xs text-slate-600 md:text-right md:min-w-[240px]">
                        <div>
                          <span className="font-bold text-slate-900">{r.redirect_uris_count}</span> redirect URI{r.redirect_uris_count === 1 ? "" : "s"}
                          {r.has_prod_redirect ? (
                            <span className="ml-2 text-emerald-700">✓ prod</span>
                          ) : (
                            <span className="ml-2 text-orange-700">✗ prod missing</span>
                          )}
                        </div>
                        {r.latest_grant ? (
                          <div className="mt-1 text-[11px] text-slate-500 truncate" title={r.latest_grant.host}>
                            preview: {r.latest_grant.host} · {r.latest_grant.hits} hits · {fmtRelative(r.latest_grant.last_seen_at)}
                          </div>
                        ) : (
                          <div className="mt-1 text-[11px] text-slate-400">no preview SSO yet</div>
                        )}
                        {r.native_dedupe.heavy && (
                          <div className="mt-1 text-[11px] text-violet-600 truncate" title={r.native_dedupe.index_url}>
                            /api/native/index → {r.native_dedupe.index_url}
                          </div>
                        )}
                        <div className="mt-2 flex gap-2 md:justify-end flex-wrap">
                          {r.status === "pending" ? (
                            <button
                              type="button"
                              onClick={() => mintClient(r.slug)}
                              disabled={minting === r.slug}
                              data-testid={`admin-sisters-mint-${r.slug}`}
                              className="inline-flex items-center gap-1 rounded-full bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-[11px] font-semibold px-3 py-1"
                            >
                              {minting === r.slug ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                              Mint {r.client_id}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => auditSister(r.slug)}
                              disabled={auditingSlug === r.slug || auditingAll}
                              data-testid={`admin-sisters-audit-${r.slug}`}
                              title="Run the 5-step OIDC PKCE audit against this sister"
                              className="inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-[11px] font-semibold px-3 py-1"
                            >
                              {auditingSlug === r.slug ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />}
                              Audit SSO
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* expandable audit step details */}
                    {isExpanded && (fullAudit || sso) && (
                      <div className="mt-3 rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3" data-testid={`admin-sisters-audit-details-${r.slug}`}>
                        {fullAudit?.steps ? (
                          <ul className="space-y-1.5">
                            {fullAudit.steps.map((step, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-[11px]">
                                {step.pass ? (
                                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                                ) : (
                                  <XCircle className="w-3.5 h-3.5 text-rose-600 mt-0.5 flex-shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-slate-800">
                                    {step.name}
                                    <span className={`ml-2 font-mono ${step.pass ? "text-emerald-700" : "text-rose-700"}`}>
                                      HTTP {step.status_code || "—"}
                                    </span>
                                  </div>
                                  <div className="text-slate-500">Expected: {step.expected}</div>
                                  {step.note && <div className={`mt-0.5 ${step.pass ? "text-slate-500" : "text-rose-600 font-medium"}`}>{step.note}</div>}
                                </div>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="text-[11px] text-slate-500">
                            Cached verdict: <strong className={SSO_VERDICT_STYLE[sso.verdict]?.txt}>{sso.verdict}</strong> ·
                            {" "}{sso.pass_count}/{sso.total_steps} passed · {fmtRelative(sso.checked_at)}.
                            {" "}Click <button type="button" onClick={() => auditSister(r.slug)} className="underline hover:text-slate-800">Audit SSO</button> to see per-step details.
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <p className="text-[11px] text-slate-400 mt-3 text-center">
          {rows?.length ?? "…"} sisters loaded · data fused from <code>oauth_clients</code> + <code>oidc_auto_preview_log</code> + registry.
        </p>
      </main>
      <SocialPulseFooter />
    </div>
  );
}

function SummaryPill({ icon, label, value, tone }) {
  const toneMap = {
    slate:   { bg: "bg-slate-50",   ring: "ring-slate-200",   txt: "text-slate-700",   ico: "text-slate-400"   },
    emerald: { bg: "bg-emerald-50", ring: "ring-emerald-200", txt: "text-emerald-700", ico: "text-emerald-500" },
    amber:   { bg: "bg-amber-50",   ring: "ring-amber-200",   txt: "text-amber-700",   ico: "text-amber-500"   },
    orange:  { bg: "bg-orange-50",  ring: "ring-orange-200",  txt: "text-orange-700",  ico: "text-orange-500"  },
    rose:    { bg: "bg-rose-50",    ring: "ring-rose-200",    txt: "text-rose-700",    ico: "text-rose-500"    },
    violet:  { bg: "bg-violet-50",  ring: "ring-violet-200",  txt: "text-violet-700",  ico: "text-violet-500"  },
  };
  const t = toneMap[tone] || toneMap.slate;
  return (
    <div className={`rounded-2xl ${t.bg} ring-1 ${t.ring} p-3 flex flex-col`} data-testid={`admin-sisters-summary-${label.toLowerCase().replace(/\W/g, "-")}`}>
      <div className={`flex items-center gap-1.5 ${t.ico}`}>
        {icon}
        <span className={`text-[10px] font-bold uppercase tracking-wide ${t.txt}`}>{label}</span>
      </div>
      <div className={`text-2xl font-serif font-bold ${t.txt} mt-1`}>{value}</div>
    </div>
  );
}
